import { Component, OnInit } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Router } from '@angular/router';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { TipoConferenciaEnum } from '../../../../shared/enums/compras/tipo-conferencia.enum';
import { EtapaKanbanStruct } from '../../../../shared/services/structs/compras/etapa-kanban.struct';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { EtapasKanbanRequest } from '../../../../shared/services/requests/compras/etapas-kanban.request';
import { ConfiguracaoEtapaCompletaRequest } from '../../../../shared/services/requests/compras/configuracao-etapa-completa.request';
import { CreateEtapasKanbanResponse } from '../../../../shared/services/responses/compras/create-etapas-kanban.response';
import { EtapasKanbanService } from '../../../../shared/services/apis/compras/etapas-kanban.service';
import { EtapaKanbanModalComponent, EtapaKanbanModalResult } from './dialogs/etapa-kanban-modal/etapa-kanban-modal.component';

@Component({
  selector: 'app-config-kanban-compras',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    HeaderComponent,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatDialogModule,
    DragDropModule,
    EmptyListComponent,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './config-kanban-compras.component.html',
  styleUrls: ['./config-kanban-compras.component.css'],
})
export class ConfigKanbanComprasComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Suprimentos;

  /** Id da etapa criada na sessão atual do modal, para o retry atualizar em vez de recriar. */
  private idEtapaCriadaNoModal = 0;
  public etapasPrincipais: EtapaKanbanStruct[] = [];
  public etapasEspeciais: EtapaKanbanStruct[] = [];
  public _isLoading = false;
  /** Reordenação em voo: o arraste fica desligado até a resposta para não disparar PUTs concorrentes. */
  public isSalvandoOrdenacao = false;
  /** Etapas com o toggle de ativação em voo. */
  public etapasAlternando = new Set<number>();

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private alertService: AlertService,
    private etapasKanbanService: EtapasKanbanService
  ) {}

  ngOnInit(): void {
    this.carregarEtapas();
  }

  // Cego em qualquer uma das duas conferências já marca a etapa como cega na listagem.
  public isModoCego(etapa: EtapaKanbanStruct): boolean {
    return etapa.idTipoConferenciaVolumes === TipoConferenciaEnum.Cega
      || etapa.idTipoConferenciaItens === TipoConferenciaEnum.Cega;
  }

  // ----------------------------------------------------------------------
  // Ações
  // ----------------------------------------------------------------------
  /**
   * Etapa padrão tem posição fixa: o arraste só pode cair na posição de outra etapa criada pelo
   * usuário. Assim as padrão nunca mudam de lugar nem de ordem.
   */
  public podeSoltarNaPosicao = (index: number): boolean => !this.etapasPrincipais[index]?.isFixo;

  /**
   * As etapas criadas pelo usuário trocam entre si as ordens que já tinham; as padrão não são
   * renumeradas. Só as etapas cuja ordem mudou vão para o servidor.
   */
  public drop(event: CdkDragDrop<EtapaKanbanStruct[]>): void {
    if (event.previousIndex === event.currentIndex || this.isSalvandoOrdenacao) return;

    const ordensLivres = this.etapasPrincipais
      .filter(etapa => !etapa.isFixo)
      .map(etapa => etapa.ordem)
      .sort((a, b) => a - b);
    const ordemAnterior = new Map(this.etapasPrincipais.map(etapa => [etapa.idEtapaKanban, etapa.ordem]));

    moveItemInArray(this.etapasPrincipais, event.previousIndex, event.currentIndex);
    this.etapasPrincipais
      .filter(etapa => !etapa.isFixo)
      .forEach((etapa, index) => (etapa.ordem = ordensLivres[index]));

    const alteradas = this.etapasPrincipais
      .filter(etapa => etapa.ordem !== ordemAnterior.get(etapa.idEtapaKanban))
      .map(etapa => ({ etapa, ordemAnterior: ordemAnterior.get(etapa.idEtapaKanban) ?? etapa.ordem }));
    this.salvarOrdenacao(alteradas);
  }

  public toggleAtivo(etapa: EtapaKanbanStruct, ativo: boolean): void {
    if (this.etapasAlternando.has(etapa.idEtapaKanban)) return;
    const estadoAnterior = etapa.isAtivo;
    etapa.isAtivo = ativo;
    this.etapasAlternando.add(etapa.idEtapaKanban);

    this.etapasKanbanService.update(etapa.idEtapaKanban, this.toRequest(etapa)).subscribe({
      next: response => {
        this.etapasAlternando.delete(etapa.idEtapaKanban);
        if (response.isError) {
          etapa.isAtivo = estadoAnterior;
          // A recusa de inativar traz um impedimento por linha; o alerta renderiza HTML.
          this.alertService.show('Atenção', (response.errorDescription ?? '').split('\n').join('<br>'), AlertType.warning);
          return;
        }

        this.alertService.show(
          'Etapa atualizada',
          `A etapa "${etapa.nome}" foi ${ativo ? 'ativada' : 'inativada'}.`,
          AlertType.success
        );
        this.carregarEtapas();
      },
      error: () => {
        this.etapasAlternando.delete(etapa.idEtapaKanban);
        etapa.isAtivo = estadoAnterior;
        this.alertService.show('Erro', 'Não foi possível atualizar a etapa.', AlertType.error);
      }
    });
  }

  public novaEtapa(): void {
    this.abrirModalEtapa(null);
  }

  public editarEtapa(etapa: EtapaKanbanStruct): void {
    this.abrirModalEtapa(etapa);
  }

  private abrirModalEtapa(etapa: EtapaKanbanStruct | null): void {
    // Zera o id de criação da sessão anterior do modal (ver salvarEtapaDoModal).
    this.idEtapaCriadaNoModal = 0;

    const dialogRef = this.dialog.open(EtapaKanbanModalComponent, {
      width: '860px',
      maxWidth: '96vw',
      autoFocus: false,
      panelClass: 'etapa-kanban-dialog',
      data: {
        etapa,
        todasEtapas: [...this.etapasPrincipais, ...this.etapasEspeciais],
        onSalvar: (dados: EtapaKanbanModalResult) => this.salvarEtapaDoModal(dados),
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;

      if (result.excluiu && result.etapa) {
        // O modal já pediu a mesma confirmação da lista antes de fechar.
        this.executarExclusao(result.etapa);
        return;
      }

      if (result.salvou) this.carregarEtapas();
    });
  }

  private salvarEtapaDoModal(dados: EtapaKanbanModalResult): Observable<boolean> {
    const salva = { ...dados.etapa };
    // O id vem daqui, e não de `dados.etapa`: o modal remonta o payload a cada clique, então
    // gravar o id nele se perdia e o retry (após falha nas sub-configurações) repetia o POST,
    // que o backend recusa com "Já existe uma etapa com essa chave".
    const idExistente = salva.idEtapaKanban || this.idEtapaCriadaNoModal;
    const criando = dados.isNova && !idExistente;
    // Etapa nova entra no fim do fluxo principal com ordem acima de todas as existentes: as
    // especiais (cancelado 8, divergência 9) ocupam as ordens logo após Finalizado e não há inteiro
    // livre entre elas sem renumerar uma padrão. O quadro e o backend tratam as especiais à parte.
    if (criando) salva.ordem = Math.max(0, ...[...this.etapasPrincipais, ...this.etapasEspeciais].map(e => e.ordem)) + 1;

    // Na criação, o POST só serve para a etapa nascer com um id; toda a configuração vai no
    // salvamento composto logo em seguida. Na edição é uma chamada só.
    const requisicao = criando
      ? this.etapasKanbanService.create(this.toRequest(salva))
      : of({ isError: false, errorCode: 0, errorDescription: '', etapa: { idEtapaKanban: idExistente } } as CreateEtapasKanbanResponse);

    return requisicao.pipe(
      switchMap(response => {
        const idEtapa = criando ? response.etapa?.idEtapaKanban : idExistente;
        if (response.isError || !idEtapa) {
          this.alertService.show('Atenção', response.errorDescription || 'Não foi possível salvar a etapa.', AlertType.warning);
          return of(false);
        }

        if (criando) this.idEtapaCriadaNoModal = idEtapa;
        dados.etapa.idEtapaKanban = idEtapa;

        return this.persistirSubConfiguracoesDoModal(
          idEtapa,
          { ...salva, idEtapaKanban: idEtapa },
          dados,
          `Etapa "${salva.nome}" ${criando ? 'criada' : 'atualizada'} com sucesso.`
        );
      }),
      catchError(() => {
        this.alertService.show('Erro', 'Não foi possível salvar a etapa.', AlertType.error);
        return of(false);
      })
    );
  }

  private persistirSubConfiguracoesDoModal(
    idEtapaKanban: number,
    etapa: EtapaKanbanStruct,
    dados: EtapaKanbanModalResult,
    mensagemSucesso: string
  ): Observable<boolean> {
    const request: ConfiguracaoEtapaCompletaRequest = {
      etapa: this.toRequest(etapa),
      campos: dados.campos,
      camposNativos: dados.camposNativos,
      movimentacoes: dados.movimentacoes,
      permissoes: dados.permissoes,
      regras: dados.regras,
    };

    return this.etapasKanbanService.salvarConfiguracaoCompleta(idEtapaKanban, request).pipe(
      map(resposta => {
        if (resposta.isError) {
          // A recusa de inativar traz um impedimento por linha; o alerta renderiza HTML.
          this.alertService.show('Atenção',
            (resposta.errorDescription || 'Não foi possível salvar a configuração da etapa.').split('\n').join('<br>'),
            AlertType.warning);
          return false;
        }

        // O resto da configuração foi gravado, mas os destinos recusados não: devolver false
        // mantém o modal aberto para o usuário ver o que ficou de fora e corrigir.
        if (resposta.movimentacoesRecusadas?.length) {
          this.alertService.show('Atenção',
            'Movimentações não permitidas: ' + resposta.movimentacoesRecusadas.join('; ') + '.',
            AlertType.warning);
          return false;
        }

        this.alertService.show('Sucesso', mensagemSucesso, AlertType.success);
        return true;
      }),
      catchError(() => {
        this.alertService.show('Atenção', 'Não foi possível salvar a configuração da etapa.', AlertType.error);
        return of(false);
      })
    );
  }

  public excluirEtapa(etapa: EtapaKanbanStruct): void {
    if (etapa.isFixo) {
      this.alertService.show('Atenção', 'Etapas padrão do fluxo não podem ser excluídas.', AlertType.warning);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmar exclusão',
        description: `A etapa "${etapa.nome}" será removida do fluxo. Confirma a exclusão?`,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.confirm) return;
      this.executarExclusao(etapa);
    });
  }

  /**
   * Exclusão no molde das etapas da venda: sem vínculos, a etapa é apagada; com vínculos, a API não altera
   * nada e devolve possuiVinculos com a pergunta. Aceitando, a etapa é inativada (EtapasKanban/inativar).
   * Se a inativação seria recusada (compras ativas na etapa, automação ligada com destino nela), a API
   * já responde sem possuiVinculos, com uma mensagem por linha, e a tela só mostra o erro.
   */
  private executarExclusao(etapa: EtapaKanbanStruct): void {
    if (etapa.isFixo) {
      this.alertService.show('Atenção', 'Etapas padrão do fluxo não podem ser excluídas.', AlertType.warning);
      return;
    }

    this.etapasKanbanService.delete(etapa.idEtapaKanban).subscribe({
      next: response => {
        if (response.isError && response.possuiVinculos) {
          this.dialog.open(ConfirmModalComponent, {
            maxWidth: '96vw',
            data: {
              title: 'Exclusão não permitida',
              description: response.errorDescription,
              buttonConfirm: 'Inativar etapa',
              buttonClose: 'Cancelar',
            },
          }).afterClosed().subscribe(confirmacao => {
            if (!confirmacao?.confirm) return;

            this.etapasKanbanService.inativar(etapa.idEtapaKanban).subscribe({
              next: resposta => {
                if (resposta.isError) {
                  this.alertService.show('Erro', (resposta.errorDescription ?? '').split('\n').join('<br>'), AlertType.error);
                  return;
                }

                this.alertService.show('Sucesso', 'Etapa inativada com sucesso.', AlertType.success);
                this.carregarEtapas();
              },
              error: () => this.alertService.show('Erro', 'Não foi possível inativar a etapa.', AlertType.error)
            });
          });
          return;
        }
        if (response.isError) {
          // A recusa traz um impedimento por linha; o alerta renderiza HTML.
          this.alertService.show('Erro', (response.errorDescription ?? '').split('\n').join('<br>'), AlertType.error);
          return;
        }

        this.alertService.show('Sucesso', 'Etapa excluída com sucesso.', AlertType.success);
        this.carregarEtapas();
      },
      error: () => this.alertService.show('Erro', 'Não foi possível excluir a etapa.', AlertType.error)
    });
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/suprimentos']);
  }

  private carregarEtapas(): void {
    this._isLoading = true;

    this.etapasKanbanService.getAll('').subscribe({
      next: response => {
        this._isLoading = false;

        if (response.isError) {
          this.alertService.show('Atenção', response.errorDescription, AlertType.warning);
          return;
        }

        this.aplicarEtapas(response.listaEtapas ?? []);
      },
      error: () => {
        this._isLoading = false;
        this.alertService.show('Erro', 'Não foi possível carregar as etapas do kanban.', AlertType.error);
      }
    });
  }

  private aplicarEtapas(etapas: EtapaKanbanStruct[]): void {
    const normalizadas = etapas
      .map(etapa => this.normalizarEtapa(etapa))
      .sort((a, b) => a.ordem - b.ordem || a.nome.localeCompare(b.nome));

    this.etapasPrincipais = normalizadas.filter(etapa => !etapa.isEspecial);
    this.etapasEspeciais = normalizadas.filter(etapa => etapa.isEspecial);
  }

  // Sem cor gravada, o card e o ícone ficam com o estilo padrão do CSS (sem cor inline).
  // `tint` é só de tela (fundo do ícone na lista e nos destinos do modal); não vem da API.
  private normalizarEtapa(etapa: EtapaKanbanStruct): EtapaKanbanStruct & { tint: string } {
    const cor = etapa.cor || '';
    return {
      ...etapa,
      descricao: etapa.descricao ?? '',
      cor,
      tint: cor ? `${cor}22` : '',
      icone: etapa.icone || 'description',
      qtdCampos: etapa.qtdCampos ?? 0,
      qtdDestinos: etapa.qtdDestinos ?? 0,
      isFixo: etapa.isFixo ?? false,
    };
  }

  private toRequest(etapa: EtapaKanbanStruct): EtapasKanbanRequest {
    return {
      chave: etapa.chave,
      nome: etapa.nome,
      descricao: etapa.descricao || null,
      cor: etapa.cor || null,
      icone: etapa.icone || null,
      ordem: etapa.ordem,
      isOpcional: etapa.isOpcional,
      isAtivo: etapa.isAtivo,
      isEspecial: etapa.isEspecial,
      isFinal: etapa.isFinal,
      idTipoConferenciaVolumes: etapa.idTipoConferenciaVolumes,
      idTipoConferenciaItens: etapa.idTipoConferenciaItens,
      idNivelGarantido: etapa.idNivelGarantido ?? null,
      idNivelRequisito: etapa.idNivelRequisito ?? null,
      idRegraProdutoNaoCadastrado: etapa.idRegraProdutoNaoCadastrado,
      idRegraToleranciaPreco: etapa.idRegraToleranciaPreco,
      toleranciaPrecoPercentual: etapa.toleranciaPrecoPercentual,
      idRegraToleranciaQuantidade: etapa.idRegraToleranciaQuantidade,
      toleranciaQuantidadePercentual: etapa.toleranciaQuantidadePercentual,
    };
  }

  /**
   * Um único lote de PUTs por arraste, com o arraste travado até a resposta. Se parte falhar,
   * desfaz no servidor as que gravaram (senão ficariam duas etapas com a mesma ordem) e recarrega.
   */
  private salvarOrdenacao(alteradas: { etapa: EtapaKanbanStruct; ordemAnterior: number }[]): void {
    if (alteradas.length === 0) return;

    const gravarOrdem = (etapa: EtapaKanbanStruct, ordem: number): Observable<boolean> =>
      this.etapasKanbanService.update(etapa.idEtapaKanban, this.toRequest({ ...etapa, ordem })).pipe(
        map(response => !response.isError),
        catchError(() => of(false))
      );

    this.isSalvandoOrdenacao = true;
    forkJoin(alteradas.map(item => gravarOrdem(item.etapa, item.etapa.ordem))).pipe(
      switchMap(resultados => {
        if (resultados.every(ok => ok)) return of(true);

        const gravadas = alteradas.filter((_, index) => resultados[index]);
        if (gravadas.length === 0) return of(false);
        return forkJoin(gravadas.map(item => gravarOrdem(item.etapa, item.ordemAnterior))).pipe(map(() => false));
      })
    ).subscribe(salvou => {
      this.isSalvandoOrdenacao = false;
      if (salvou) return;

      this.alertService.show('Erro', 'Não foi possível salvar a ordenação.', AlertType.error);
      this.carregarEtapas();
    });
  }
}
